// import { useNavigate } from "react-router-dom";

import React from "react";

import {
  Button,
  Card,
  CardFooter,
  Checkbox,
  DatePicker,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  Label,
  PermissionGuard,
  RequiredLabel,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components";
import { useI18n } from "@/context";
import { formatCpfCnpj } from "@/core/utils";

import { ITeamFormProps } from "./team.hooks.interface";

export const RenderTeamCard: React.FC<ITeamFormProps> = ({
  form,
  setTab,
  tab,
  handleAddressBlur,
  roles,
  uniquePermissions,
  onSubmit,
}) => {
  const { t } = useI18n();
  const isEditing = Boolean(
    new URLSearchParams(window.location.search).get("id")
  );

  return (
    <Card className="p-4 w-full max-w-full">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <Tabs
            value={tab}
            onValueChange={(value) =>
              setTab(value as "data" | "login" | "permission")
            }
            defaultValue="data"
            className="w-full"
          >
            <TabsList className="w-full flex justify-between">
              <TabsTrigger value="data" className="flex-1">
                {t("users.tabs.data")}
              </TabsTrigger>
              <TabsTrigger value="login" className="flex-1">
                {t("users.tabs.login")}
              </TabsTrigger>
              <TabsTrigger value="permission" className="flex-1">
                {t("users.tabs.permissions")}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="data">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <FormField
                  control={form.control}
                  name="data.firstName"
                  render={({ field }) => (
                    <FormItem className="mt-4">
                      <RequiredLabel required>
                        {t("users.fields.firstName")}
                      </RequiredLabel>
                      <FormControl>
                        <Input {...field} id="data.firstName" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="data.lastName"
                  render={({ field }) => (
                    <FormItem className="mt-4">
                      <RequiredLabel required>
                        {t("users.fields.lastName")}
                      </RequiredLabel>
                      <FormControl>
                        <Input {...field} id="data.lastName" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  name="data.optionCpfCnpj"
                  render={({ field }) => (
                    <FormItem className="mt-4">
                      <RequiredLabel required htmlFor="documentType">
                        {t("users.fields.documentType")}
                      </RequiredLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value || ""}
                        >
                          <SelectTrigger className="w-[100%]">
                            <SelectValue
                              placeholder={t(
                                "products.form.fields.insurance_company_id.placeholder"
                              )}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              <SelectItem value="cpf">CPF</SelectItem>
                              <SelectItem value="cnpj">CNPJ</SelectItem>
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  name="data.document"
                  render={({ field }) => (
                    <FormItem className="mt-4">
                      <RequiredLabel htmlFor="document" required>
                        {t("users.fields.document")}
                      </RequiredLabel>
                      <FormControl>
                        <Input
                          id="data.document"
                          value={formatCpfCnpj(field.value || "")}
                          onChange={(e) => {
                            const onlyDigits = e.target.value.replace(
                              /\D/g,
                              ""
                            );
                            field.onChange(onlyDigits);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="data.birthDate"
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel htmlFor="birthDate" required>
                        {t("users.fields.birthDate")}
                      </RequiredLabel>
                      <FormControl>
                        <DatePicker
                          value={
                            field.value ? new Date(field.value) : undefined
                          }
                          onChange={(date) => {
                            field.onChange(date);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="data.phone"
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel htmlFor="phone" required>
                        {t("users.fields.phone")}
                      </RequiredLabel>
                      <FormControl>
                        <Input type="tel" {...field} id="data.phone" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="data.zipcode"
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel htmlFor="zipcode" required>
                        {t("users.fields.zipcode")}
                      </RequiredLabel>
                      <FormControl>
                        <Input
                          {...field}
                          onBlur={handleAddressBlur}
                          id="data.zipcode"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="data.state"
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel htmlFor="state" required>
                        {t("users.fields.state")}
                      </RequiredLabel>
                      <FormControl>
                        <Input {...field} id="data.state" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="data.city"
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel htmlFor="city" required>
                        {t("users.fields.city")}
                      </RequiredLabel>
                      <FormControl>
                        <Input {...field} id="data.city" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="data.neighborhood"
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel htmlFor="neighborhood" required>
                        {t("users.fields.neighborhood")}
                      </RequiredLabel>
                      <FormControl>
                        <Input {...field} id="data.neighborhood" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="data.street"
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel htmlFor="street" required>
                        {t("users.fields.street")}
                      </RequiredLabel>
                      <FormControl>
                        <Input {...field} id="data.street" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="data.number"
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel htmlFor="number" required>
                        {t("users.fields.number")}
                      </RequiredLabel>
                      <FormControl>
                        <Input {...field} id="data.number" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="data.complement"
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel htmlFor="complement">
                        {t("users.fields.complement")}
                      </RequiredLabel>
                      <FormControl>
                        <Input {...field} id="data.complement" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <CardFooter className="justify-end">
                <Button type="button" onClick={() => setTab("login")}>
                  {t("generic.next")}
                </Button>
              </CardFooter>
            </TabsContent>

            <TabsContent value="login">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <FormField
                  control={form.control}
                  name="login.email"
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel required>
                        {t("users.fields.email")}
                      </RequiredLabel>
                      <FormControl>
                        <Input {...field} id="login.email" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="login.password"
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel required={!isEditing}>
                        {t("users.fields.password")}
                      </RequiredLabel>
                      <FormControl>
                        <Input {...field} id="login.password" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <CardFooter className="justify-end gap-4 mt-5">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setTab("data")}
                >
                  {t("generic.previous")}
                </Button>
                <Button type="button" onClick={() => setTab("permission")}>
                  {t("generic.next")}
                </Button>
              </CardFooter>
            </TabsContent>

            <TabsContent value="permission">
              <div>
                <div className="mt-4">
                  <FormField
                    name="permissions.accessLevel"
                    render={({ field }) => (
                      <FormItem className="mt-2 space-y-3 w-1/4">
                        <RequiredLabel required htmlFor="accessLevel">
                          {t("users.fields.selectAccessLevel")}:
                        </RequiredLabel>
                        <FormControl>
                          <Select
                            value={field.value}
                            onValueChange={(value) => {
                              field.onChange(value);
                              const selectedRole = roles.find(
                                (r) => r.name === value
                              );
                              if (selectedRole) {
                                const rolePermissions =
                                  selectedRole.permissions.map((p) => p.name);
                                form.setValue(
                                  "permissions.selectedPermissions",
                                  rolePermissions,
                                  {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                  }
                                );
                              } else {
                                form.setValue(
                                  "permissions.selectedPermissions",
                                  [],
                                  {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                  }
                                );
                              }
                            }}
                          >
                            <SelectTrigger className="w-full h-12">
                              <SelectValue
                                placeholder={t(
                                  "users.fields.selectAccessLevel"
                                )}
                              />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                {roles.map((role) => (
                                  <SelectItem key={role.id} value={role.name}>
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium">
                                        {role.name}
                                      </span>
                                      <span>-</span>
                                      <span className="text-sm text-muted-foreground">
                                        {role.description}
                                      </span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="permissions.selectedPermissions"
                    render={({ field }) => {
                      const [searchQuery, setSearchQuery] = React.useState("");
                      const [activeTab, setActiveTab] = React.useState<string>("view");

                      // Agrupar permissões por tipo de ação
                      const groupedByAction = React.useMemo(() => {
                        const groups: Record<string, typeof uniquePermissions> = {
                          list: [],
                          view: [],
                          create: [],
                          update: [],
                          delete: [],
                          other: [],
                        };

                        uniquePermissions.forEach((permission) => {
                          const name = permission.name.toLowerCase();
                          
                          // Extrair ação do formato "resource:action" (ex: "products:create" -> "create")
                          let action = "";
                          if (name.includes(":")) {
                            const parts = name.split(":");
                            action = parts[parts.length - 1]?.trim() || "";
                          }

                          // Normalizar "edit" para "update"
                          if (action === "edit") {
                            action = "update";
                          }

                          // Normalizar sinônimos
                          if (action === "cadastrar") {
                            action = "create";
                          } else if (action === "acessar") {
                            action = "view";
                          }

                          // Categorizar baseado na ação extraída
                          if (action === "list") {
                            groups.list.push(permission);
                          } else if (action === "view") {
                            groups.view.push(permission);
                          } else if (action === "create") {
                            groups.create.push(permission);
                          } else if (action === "update") {
                            groups.update.push(permission);
                          } else if (action === "delete") {
                            groups.delete.push(permission);
                          } else {
                            // Se não tem ":", tentar categorizar pelo nome completo
                            if (name.includes("list") || name.includes("listar")) {
                              groups.list.push(permission);
                            } else if (name.includes("view") || name.includes("visualizar") || name.includes("acessar")) {
                              groups.view.push(permission);
                            } else if (name.includes("create") || name.includes("criar") || name.includes("cadastrar")) {
                              groups.create.push(permission);
                            } else if (name.includes("update") || name.includes("edit") || name.includes("atualizar") || name.includes("editar")) {
                              groups.update.push(permission);
                            } else if (name.includes("delete") || name.includes("deletar")) {
                              groups.delete.push(permission);
                            } else {
                              groups.other.push(permission);
                            }
                          }
                        });

                        return groups;
                      }, [uniquePermissions]);

                      // Filtrar permissões de todas as abas pela busca
                      const filteredGroups = React.useMemo(() => {
                        if (!searchQuery.trim()) {
                          return groupedByAction;
                        }

                        const filtered: Record<string, typeof uniquePermissions> = {
                          list: [],
                          view: [],
                          create: [],
                          update: [],
                          delete: [],
                          other: [],
                        };

                        const query = searchQuery.toLowerCase();
                        Object.keys(groupedByAction).forEach((action) => {
                          filtered[action] = (groupedByAction[action] || []).filter(
                            (perm) =>
                              perm.name.toLowerCase().includes(query) ||
                              perm.description.toLowerCase().includes(query)
                          );
                        });

                        return filtered;
                      }, [groupedByAction, searchQuery]);

                      // Definir a primeira aba com conteúdo como padrão
                      React.useEffect(() => {
                        const tabOrder = ["view", "list", "create", "update", "delete", "other"];
                        // Se há busca, usar grupos filtrados, senão usar grupos originais
                        const groupsToCheck = searchQuery.trim() ? filteredGroups : groupedByAction;
                        const firstTabWithContent = tabOrder.find(
                          (key) => (groupsToCheck[key]?.length || 0) > 0
                        );
                        if (firstTabWithContent && (!groupsToCheck[activeTab]?.length)) {
                          setActiveTab(firstTabWithContent);
                        }
                      }, [groupedByAction, filteredGroups, activeTab, searchQuery]);

                      // Filtrar permissões da aba ativa
                      const filteredPermissions = React.useMemo(() => {
                        return filteredGroups[activeTab] || [];
                      }, [filteredGroups, activeTab]);

                      const actionLabels: Record<string, string> = {
                        list: "Listar",
                        view: "Visualizar",
                        create: "Criar",
                        update: "Editar",
                        delete: "Excluir",
                        other: "Outras",
                      };

                      const toggleAction = (checked: boolean) => {
                        const currentPermissions = field.value || [];
                        if (checked) {
                          const newPerms = filteredPermissions
                            .map((p) => p.name)
                            .filter((name) => !currentPermissions.includes(name));
                          field.onChange([...currentPermissions, ...newPerms]);
                        } else {
                          const filteredPermNames = filteredPermissions.map((p) => p.name);
                          field.onChange(
                            currentPermissions.filter((name) => !filteredPermNames.includes(name))
                          );
                        }
                      };

                      const isActionAllSelected = () => {
                        if (filteredPermissions.length === 0) return false;
                        const currentPermissions = field.value || [];
                        return filteredPermissions.every((perm) =>
                          currentPermissions.includes(perm.name)
                        );
                      };

                      const getTabCount = (action: string) => {
                        // Usar grupos filtrados quando há busca, senão usar grupos originais
                        const actionPerms = (searchQuery.trim() ? filteredGroups : groupedByAction)[action] || [];
                        const currentPermissions = field.value || [];
                        return actionPerms.filter((perm) =>
                          currentPermissions.includes(perm.name)
                        ).length;
                      };

                      const getTabTotalCount = (action: string) => {
                        // Retorna o total de permissões na aba (filtradas ou não)
                        const actionPerms = (searchQuery.trim() ? filteredGroups : groupedByAction)[action] || [];
                        return actionPerms.length;
                      };

                      // Lista de abas em ordem específica
                      const tabOrder = ["view", "list", "create", "update", "delete", "other"];

                      const isAllSelected = React.useMemo(() => {
                        const currentPermissions = field.value || [];
                        return (
                          uniquePermissions.length > 0 &&
                          uniquePermissions.every((permission) =>
                            currentPermissions.includes(permission.name)
                          )
                        );
                      }, [uniquePermissions, field.value]);

                      const toggleAllPermissions = (checked: boolean) => {
                        if (checked) {
                          field.onChange(uniquePermissions.map((permission) => permission.name));
                        } else {
                          field.onChange([]);
                        }
                      };

                      return (
                      <FormItem>
                          <div className="mt-4 space-y-3">
                            <div className="flex items-center justify-between gap-4">
                              <p className="text-sm font-medium">
                            {t("users.permissions.orSelectIndividually")}
                          </p>
                              <span className="text-xs text-muted-foreground">
                                {field.value?.length || 0} / {uniquePermissions.length} selecionadas
                              </span>
                            </div>

                            <div className="flex items-center gap-2 p-2 bg-muted/30 rounded-md">
                            <Checkbox
                              id="selectAllPermissions"
                                checked={isAllSelected}
                                onCheckedChange={toggleAllPermissions}
                              />
                              <label
                                htmlFor="selectAllPermissions"
                                className="text-sm font-medium cursor-pointer"
                              >
                                Selecionar todas as permissões
                              </label>
                            </div>

                            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                              <TabsList className="grid w-full grid-cols-6">
                                {tabOrder.map((action) => {
                                  const count = getTabTotalCount(action);
                                  // Mostrar aba se tiver conteúdo (filtrado ou não)
                                  if (count === 0 && !searchQuery.trim()) {
                                    // Se não há busca, só mostrar abas com conteúdo original
                                    const originalCount = groupedByAction[action]?.length || 0;
                                    if (originalCount === 0) return null;
                                  }
                                  return (
                                    <TabsTrigger
                                      key={action}
                                      value={action}
                                      className="relative text-xs"
                                      disabled={count === 0 && searchQuery.trim() !== ""}
                                    >
                                      {actionLabels[action]}
                                      {getTabCount(action) > 0 && (
                                        <span className="ml-1 text-xs font-semibold">
                                          ({getTabCount(action)})
                                        </span>
                                      )}
                                      {searchQuery.trim() && count > 0 && (
                                        <span className="ml-1 text-xs text-muted-foreground">
                                          [{count}]
                                        </span>
                                      )}
                                    </TabsTrigger>
                                  );
                                })}
                              </TabsList>

                              <div className="mt-3 space-y-3">
                                <div className="flex items-center gap-2">
                                  <Input
                                    placeholder={t("generic.search")}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="flex-1"
                                  />
                                  <Checkbox
                                    id="selectAllTabPermissions"
                                    checked={isActionAllSelected()}
                                    onCheckedChange={toggleAction}
                                  />
                                  <label
                                    htmlFor="selectAllTabPermissions"
                                    className="text-sm cursor-pointer whitespace-nowrap"
                                  >
                              {t("users.permissions.selectAll")}
                            </label>
                          </div>

                                <TabsContent value={activeTab} className="mt-0">
                                  <div className="border rounded-md max-h-[350px] overflow-y-auto">
                                    {filteredPermissions.length === 0 ? (
                                      <p className="text-sm text-muted-foreground text-center py-8">
                                        {searchQuery.trim()
                                          ? t("generic.noResultsFound")
                                          : "Nenhuma permissão nesta categoria"}
                                      </p>
                                    ) : (
                                      <div className="divide-y">
                                        {filteredPermissions.map((permission) => (
                            <div
                              key={permission.id}
                                            className="flex items-center gap-3 p-2 hover:bg-muted/30 transition-colors"
                            >
                              <Checkbox
                                id={permission.id}
                                checked={field.value?.includes(permission.name)}
                                onCheckedChange={(checked) => {
                                  const currentPermissions = field.value || [];
                                  return checked
                                    ? field.onChange([
                                        ...currentPermissions,
                                        permission.name,
                                      ])
                                    : field.onChange(
                                        currentPermissions.filter(
                                          (value) => value !== permission.name
                                        )
                                      );
                                }}
                              />
                                            <div className="flex-1 min-w-0">
                                              <Label
                                                htmlFor={permission.id}
                                                className="text-sm font-medium cursor-pointer block"
                                              >
                                  {permission.name}
                                </Label>
                                              <p className="text-xs text-muted-foreground line-clamp-1">
                                  {permission.description}
                                              </p>
                              </div>
                            </div>
                          ))}
                                      </div>
                                    )}
                                  </div>
                                </TabsContent>
                              </div>
                            </Tabs>
                        </div>
                        <FormMessage />
                      </FormItem>
                      );
                    }}
                  />
                </div>
                <CardFooter className="justify-end gap-4 mt-5">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setTab("login")}
                  >
                    {t("generic.previous")}
                  </Button>
                        <PermissionGuard
                          permission={isEditing ? "user:update" : "user:create"}
                        >
                          <Button type="submit">
                            {isEditing ? t("generic.edit") : t("generic.create")}
                          </Button>
                        </PermissionGuard>
                </CardFooter>
              </div>
            </TabsContent>
          </Tabs>
        </form>
      </Form>
    </Card>
  );
};
